import React, { useEffect, useState } from "react";
import { Box, Button, Typography } from "@material-ui/core";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import { ApolloError, useLazyQuery, useMutation } from "@apollo/client";
import { useSelector } from "react-redux";
import { IAppState } from "../../store";
import { ICustomer } from "../../reducers/auth/types";
import { IdentityVerificationStatus } from "../../reducers/website/types";
import { EntrustSdk } from "./EntrustSdk";
import { START_IDENTITY_VERIFICATION_PROCESS } from "../../graphql/identityVerification/entrustIdentityVerificationMutation";
import { GET_CONSUMER_CUSTOMER_BY_APPLICANT_ID } from "../../graphql/customers/getConsumerCustomerByApplicant";
import { useSnackBar } from "../SnackBarContext/SnackBarContext";
import { SnackBarVariant } from "../SnackbarWrapper/SnackbarWrapper";
import { formatGraphQLErrorMessage } from "../utils";

interface IProps {
  customer: ICustomer;
}

// NU-12935: post-registration KYC status for an individual customer's own
// account page - mirrors customer-portal-mobile's KycVerificationCard, which
// was the only channel showing this before. Registration already offers (and
// allows skipping) Entrust verification via EntrustSdk/START_IDENTITY_VERIFICATION_PROCESS;
// this reuses the same building blocks so a customer who skipped it, or whose
// result needs retrying, has somewhere to complete it later.
export const IdentityVerificationCard: React.FC<IProps> = ({ customer }) => {
  const snackbar = useSnackBar();
  const website = useSelector((state: IAppState) => state.consumerWebsiteReducer.consumerWebsite);

  const [status, setStatus] = useState<string>(customer.identityVerification?.status || "");
  const [sdkOpen, setSdkOpen] = useState(false);
  const [sdkToken, setSdkToken] = useState("");
  const [workflowRunId, setWorkflowRunId] = useState("");
  const [applicantId, setApplicantId] = useState("");
  const [isPolling, setIsPolling] = useState(false);

  useEffect(() => {
    setStatus(customer.identityVerification?.status || "");
  }, [customer.identityVerification?.status]);

  const [startIdentityVerificationProcess, { loading: starting }] = useMutation(START_IDENTITY_VERIFICATION_PROCESS, {
    onCompleted: (data) => {
      if (data?.startIdentityVerification) {
        setSdkToken(data.startIdentityVerification.sdkToken);
        setWorkflowRunId(data.startIdentityVerification.workflowRunId);
        setApplicantId(data.startIdentityVerification.applicantId);
        setSdkOpen(true);
      }
    },
    onError: (error: ApolloError) =>
      snackbar({ message: formatGraphQLErrorMessage(error.message), variant: SnackBarVariant.ERROR })
  });

  const [pollStatus] = useLazyQuery(GET_CONSUMER_CUSTOMER_BY_APPLICANT_ID, {
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      const verification = data?.consumerGetCustomerByApplicantId?.identityVerification;
      if (!verification) {
        return;
      }
      if (
        verification.status === IdentityVerificationStatus.APPROVED ||
        verification.status === "REJECTED" ||
        verification.status === IdentityVerificationStatus.IN_REVIEW
      ) {
        setStatus(verification.status);
        setIsPolling(false);
        setSdkOpen(false);
      }
    }
  });

  useEffect(() => {
    if (!isPolling || !applicantId) {
      return;
    }
    const poll = () => pollStatus({ variables: { applicantId } });
    poll();
    const interval = setInterval(poll, 3000);
    return () => clearInterval(interval);
  }, [isPolling, applicantId]);

  if (!website.tenancy?.identityVerification?.enabled) {
    return null;
  }

  const isApproved = status === IdentityVerificationStatus.APPROVED;
  const isRejected = status === "REJECTED";
  const isInReview = status === IdentityVerificationStatus.IN_REVIEW;

  const handleStart = () => {
    startIdentityVerificationProcess({
      variables: { customerId: customer.id, flow: "SDK" }
    });
  };

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      style={{
        padding: "12px 16px",
        borderRadius: 8,
        marginBottom: 16,
        backgroundColor: isApproved ? "#ECFDF3" : isRejected ? "#FEF3F2" : "#FFF4E5"
      }}
    >
      <Box display="flex" alignItems="center">
        {isApproved ? (
          <CheckCircleIcon style={{ color: "#027A48", marginRight: 8 }} />
        ) : (
          <ErrorOutlineIcon style={{ color: isRejected ? "#B42318" : "#B54708", marginRight: 8 }} />
        )}
        <Typography variant="body2" style={{ fontWeight: 600 }}>
          {isApproved
            ? "Identity verified"
            : isRejected
              ? "Identity verification failed"
              : isInReview
                ? "Identity verification under review"
                : "Identity verification required"}
        </Typography>
      </Box>
      {/* NU-12935: IN_REVIEW means the customer's submission already reached
          Entrust - nothing left for them to do, so no retry button. Staff
          resolve it via the Override status action once reviewed. */}
      {!isApproved && !isInReview && (
        <Button
          variant="outlined"
          size="small"
          disabled={starting || isPolling}
          onClick={handleStart}
        >
          {starting ? "Starting..." : isRejected ? "Retry" : "Verify now"}
        </Button>
      )}
      <EntrustSdk
        open={sdkOpen}
        sdkToken={sdkToken}
        workflowRunId={workflowRunId}
        onComplete={() => setIsPolling(true)}
        onClose={() => setSdkOpen(false)}
        onError={() => setSdkOpen(false)}
      />
    </Box>
  );
};

export default IdentityVerificationCard;
